import React, { useState, useEffect } from "react";
import { RowModel } from "@tanstack/react-table";
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import { IPart } from "../../../models/Part";
import checkEbayDisabled from "../../../utils/checkEbayDisabled";
import PartImage from "../../stock/PartImage";
import ClickToCopy from "../../shared/ClickToCopy";
import formatDateTime from "../../../utils/formatDateTime";
import { DateTime } from "luxon";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import Modal from "../../../layouts/Modal";
import useBatchListingsMutations from "../../../data/useBatchListingsMutations";
import useNotification from "../../notifications/useNotifications";
import useSettings from "../../../data/useSettings";
import { computeEbayListingTitle } from "../EbayListingTitle";
import usePartMarketplaces from "../../../data/usePartMarketplaces";


interface BulkListModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedParts: IPart[];
  onComplete?: () => void;
}

interface PartRowProps {
  part: IPart;
  isReady: boolean;
  isSelected: boolean;
  onSelect: (part: IPart) => void;
  readyStatus?: { error: string; isDisabled: boolean };
}

function PartRow({ part, isReady, isSelected, onSelect, readyStatus }: PartRowProps) {

  const handleRowClick = () => {
    if (isReady) {
      onSelect(part);
    }
  };

  const isLocal = process.env.NODE_ENV === "development";

  return (
    <div
      className={`border rounded-lg p-3 flex items-center gap-3 ${isReady
        ? 'cursor-pointer hover:bg-gray-50 transition-colors'
        : ''
        }`}
      onClick={handleRowClick}
    >
      {isReady && (
        <input
          type="checkbox"
          checked={isSelected}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(part);
          }}
          className="rounded"
        />
      )}
      <PartImage part={part} width="w-16 h-16" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium">
            {computeEbayListingTitle(part, part.category?.partCategoryDefaults?.[0])}
            {/* {(isLocal && !part.offers[0]) && <span className="text-red-400 font-mono text-xs ml-1">**</span>} */}
          </span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium">{part.category?.name}</span>
        </div>
        <div className="text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <span>Tag Number:</span>
            <ClickToCopy text={part.tagNumber} />
          </div>
          {part.vehicle?.vrm && (
            <div className="flex items-center gap-1">
              <span>VRM:</span>
              <ClickToCopy text={part.vehicle.vrm} />
            </div>
          )}
          <div>Price: £{((part.offers[0]?.auctionStartPrice ?? 0) + (part.offers[0]?.buyItNowPrice ?? 0) || part.enteredPrice || 0).toFixed(2)}</div>
        </div>
      </div>
      <div className={`px-2 py-1 rounded text-xs ${isReady
        ? 'bg-green-100 text-green-700'
        : 'bg-red-100 text-red-700 max-w-48'
        }`}>
        {isReady ? 'Ready' : readyStatus?.error}
      </div>
    </div>
  );
}

export default function BulkListModal({ open, setOpen, selectedParts, onComplete }: BulkListModalProps) {
  const { bulkPublish } = useBatchListingsMutations("BulkListModal");
  const { possibleMarketplaces } = usePartMarketplaces(undefined);
  const { settings, loading } = useSettings();
  const { addNotification } = useNotification();

  const [selectedReadyParts, setSelectedReadyParts] = useState<IPart[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [listNow, setListNow] = useState(true);
  const [listingStarted, setListingStarted] = useState(DateTime.now().plus({ hours: 1 }));

  const [canListEbay   , setCanListEbay   ] = useState(false);
  const [canListPartPro, setCanListPartPro] = useState(false);
  const [canListShopify, setCanListShopify] = useState(false);

  // marketplaces
  const [listEbay, setListEbay]       = useState(true);
  const [listPartPro, setListPartPro] = useState(true);
  const [listShopify, setListShopify] = useState(false);

  useEffect(() => {
    if (!possibleMarketplaces.isLoading && possibleMarketplaces.data) {
      const marketplaces = possibleMarketplaces.data.data;
      setCanListEbay   (marketplaces.ebay    );
      setCanListPartPro(marketplaces.partPro );
      setCanListShopify(marketplaces.shopify );
    }
  }, [possibleMarketplaces.data, possibleMarketplaces.isLoading]);

  // Clear selected parts when modal opens; reset marketplace defaults
  useEffect(() => {
    if (open) {
      setSelectedReadyParts([]);
      setListEbay(true);
      setListPartPro(true);
      setListShopify(false);
      // setListNow(true);
      // setListingStarted(DateTime.now().plus({ hours: 1 }));
    }
  }, [open]);

  if (!open) return null;

  const parts = selectedParts;

  const readyParts = parts.filter(part => {
    const defaults = part.category?.partCategoryDefaults[0];
    const readyStatus = checkEbayDisabled({
      part,
      offer: part.offers[0],
      partCategoryDefault: defaults,
      ignoreListingTime: true
    });
    return !readyStatus.isDisabled;
  });

  const notReadyParts = parts.filter(part => {
    const defaults = part.category?.partCategoryDefaults[0];
    const readyStatus = checkEbayDisabled({
      part,
      offer: part.offers[0],
      partCategoryDefault: defaults,
      ignoreListingTime: true
    });
    return readyStatus.isDisabled;
  });

  const handleSelectAllReady = () => {
    if (selectedReadyParts.length === readyParts.length) {
      setSelectedReadyParts([]);
    } else {
      setSelectedReadyParts(readyParts);
    }
  };

  const handleSelectReadyPart = (part: IPart) => {
    setSelectedReadyParts(prev =>
      prev.includes(part)
        ? prev.filter(p => p !== part)
        : [...prev, part]
    );
  };

  const getReadyStatus = (part: IPart) => {
    const defaults = part.category?.partCategoryDefaults[0];
    return checkEbayDisabled({
      part,
      offer: part.offers[0],
      partCategoryDefault: defaults,
      ignoreListingTime: true
    });
  };


  const onSubmit = () => {
    // selectedReadyParts
    setIsLoading(true);

    const parts = selectedReadyParts.map(part => ({
      partId: part.id,
      offerId: part.offers[0]?.id ?? null,
      title: computeEbayListingTitle(part, part.category?.partCategoryDefaults?.[0])
    }));

    const body = {
      parts: parts,
      listingStarted: listNow ? DateTime.now().toISO() : listingStarted.toISO(),
      listNow: listNow,

      marketplaces: {
        ebay: canListEbay && listEbay,
        partPro: canListPartPro && listPartPro,
        shopify: canListShopify && listShopify,
      }
    };

    // console.log(body)

    bulkPublish.mutate(body, {
      onSuccess: () => {

        addNotification({
          variant: "success",
          primaryText: "Parts sent to be published successfully.",
        });

        setSelectedReadyParts([]);
        setOpen(false);
        onComplete?.();
      },
      onSettled: () => {
        setIsLoading(false);
      }
    });

  };

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-4xl w-full">
      <div className="max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Bulk List Parts</h2>
          <button
            onClick={() => setOpen(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Ready to List Section */}
          {readyParts.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  <h3 className="text-lg font-medium text-green-700">
                    Ready to List ({readyParts.length})
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedReadyParts.length === readyParts.length && readyParts.length > 0}
                    onChange={handleSelectAllReady}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-600">Select All</span>
                </div>
              </div>

              <div className="grid gap-3 max-h-[42vh] overflow-y-auto">
                {readyParts.map((part) => (
                  <PartRow
                    key={part.id}
                    part={part}
                    isReady={true}
                    isSelected={selectedReadyParts.includes(part)}
                    onSelect={handleSelectReadyPart}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Not Ready to List Section */}
          {notReadyParts.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                <h3 className="text-lg font-medium text-red-700">
                  Not Ready to List ({notReadyParts.length})
                </h3>
              </div>

              <div className="grid gap-3 max-h-[18vh] overflow-y-auto">
                {notReadyParts.map((part) => {
                  const readyStatus = getReadyStatus(part);
                  return (
                    <PartRow
                      key={part.id}
                      part={part}
                      isReady={false}
                      isSelected={false}
                      onSelect={() => { }} // No-op for not ready parts
                      readyStatus={readyStatus}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {parts.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              No parts selected
            </div>
          )}
        </div>

        {/* Listing Options Section */}
        {selectedReadyParts.length > 0 && (
          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-medium mb-3">Listing Options</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-1">
                <label className="text-sm font-medium text-gray-500">
                  List Now
                </label>
                <input
                  type="checkbox"
                  checked={listNow}
                  onChange={(e) => setListNow(e.target.checked)}
                  className="ml-1 border-1 border-gray-400"
                />
              </div>
              {!listNow && (
                <div className="sm:col-span-1">
                  <label className="text-sm font-medium text-gray-500">
                    Listing Start
                  </label>
                  <input
                    type="datetime-local"
                    value={listingStarted.toFormat("yyyy-MM-dd'T'HH:mm")}
                    onChange={(e) => {
                      const newDateTime = DateTime.fromISO(e.target.value);
                      if (newDateTime.isValid) {
                        setListingStarted(newDateTime);
                      }
                    }}
                    min={DateTime.now().toFormat("yyyy-MM-dd'T'HH:mm")}
                    className="w-full mt-1 text-sm text-gray-900 border-1 border-gray-400"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {listingStarted.toFormat("EEEE, MMMM d 'at' h:mm a")}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Listing Options Section */}
        {selectedReadyParts.length > 0 && (
          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-medium mb-3">Marketplaces</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {canListEbay && (
                <div className="sm:col-span-1">
                  <label className="text-sm font-medium text-gray-500">
                    Ebay
                  </label>
                  <input
                    type="checkbox"
                    checked={listEbay}
                    onChange={(e) => setListEbay(e.target.checked)}
                    className="ml-1 border-1 border-gray-400"
                  />
                </div>
              )}
              {canListPartPro && (
                <div className="sm:col-span-1">
                  <label className="text-sm font-medium text-gray-500">
                    Part Pro
                  </label>
                  <input
                    type="checkbox"
                    checked={listPartPro}
                    onChange={(e) => setListPartPro(e.target.checked)}
                    className="ml-1 border-1 border-gray-400"
                  />
                </div>
              )}
              {canListShopify && (
                <div className="sm:col-span-1">
                  <label className="text-sm font-medium text-gray-500">
                    Shopify
                  </label>
                  <input
                    type="checkbox"
                    checked={listShopify}
                    onChange={(e) => setListShopify(e.target.checked)}
                    className="ml-1 border-1 border-gray-400"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t pt-4 mt-4 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {selectedReadyParts.length > 0 && (
              <span>{selectedReadyParts.length} parts selected for listing</span>
            )}
          </div>
          <div className="flex gap-2">
            <ButtonInput
              label="Cancel"
              onClick={() => setOpen(false)}
              classes="border-gray-300 text-gray-600 hover:bg-gray-50"
              disabled={isLoading}
            />
            <ButtonInput
              label={isLoading ? "Processing..." : `List ${selectedReadyParts.length} Parts`}
              onClick={onSubmit}
              classes=""
              disabled={selectedReadyParts.length === 0 || isLoading}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
